/**
 * Motion for changes that arrive while Agent Tracking is open (TT-6313): rows
 * that move glide to their new place and new rows pop in, instead of the table
 * jumping. FLIP: `captureRowPositions` reads where the rows are just before a
 * live refresh is applied; `playRowMotion`, called from a layout effect once
 * React committed it, reads where they ended up and animates the difference.
 *
 * Only `transform` and `opacity` animate (compositor work, no layout), the
 * positions are read in one pass each, and nothing runs for a hidden tab,
 * under reduced motion, for rows off screen, or when too many rows would move.
 * Elements carrying `data-flip-key` must never be nested, or a child would
 * move twice.
 */

/** More visible rows than this moving at once reads as noise and costs more than it shows. */
export const MAX_MOVING_ROWS = 150;
/** Rows this far outside the viewport still count as visible, so edges never pop. */
const VIEWPORT_MARGIN = 200;
/** A capture older than this belongs to a refresh that changed nothing, not to this commit. */
const CAPTURE_TTL_MS = 1000;
const MOVE_MS = 320;
const POP_MS = 260;
const EASE_OUT = 'cubic-bezier(0.2, 0, 0, 1)';

let captured: { at: number; tops: Map<string, number> } | null = null;

export function prefersReducedMotion(win: Window = window) {
  return !!win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function flipRows(doc: Document) {
  return Array.from(doc.querySelectorAll<HTMLElement>('[data-flip-key]'));
}

/** Remembers where every flip row is; call right before applying a live refresh. */
export function captureRowPositions(doc: Document = document) {
  if (doc.hidden || prefersReducedMotion(doc.defaultView ?? window)) {
    captured = null;
    return;
  }
  const tops = new Map<string, number>();
  for (const row of flipRows(doc)) tops.set(row.dataset.flipKey!, row.getBoundingClientRect().top);
  captured = { at: Date.now(), tops };
}

/**
 * Animates the rows from where `captureRowPositions` saw them to where they are
 * now, and pops in the ones that were not there. Does nothing without a fresh
 * capture, so ordinary renders (the user's own edits) never animate.
 */
export function playRowMotion(doc: Document = document) {
  const before = captured;
  captured = null;
  if (!before || Date.now() - before.at > CAPTURE_TTL_MS || doc.hidden) return;

  // Read everything first, then write, so the browser lays out once.
  const rows = flipRows(doc).map(row => ({ row, top: row.getBoundingClientRect().top }));
  // Only what someone can see moves: a row off screen before and after needs no animation.
  const height = doc.defaultView?.innerHeight ?? 0;
  const onScreen = (top: number) => top > -VIEWPORT_MARGIN && top < height + VIEWPORT_MARGIN;
  const moves: { row: HTMLElement; dy: number }[] = [];
  const pops: HTMLElement[] = [];
  for (const { row, top } of rows) {
    const old = before.tops.get(row.dataset.flipKey!);
    if (old === undefined) {
      if (onScreen(top)) pops.push(row);
    } else if (Math.abs(old - top) >= 1 && (onScreen(old) || onScreen(top))) {
      moves.push({ row, dy: old - top });
    }
  }
  if (moves.length > MAX_MOVING_ROWS || typeof rows[0]?.row.animate !== 'function') return;

  for (const { row, dy } of moves) {
    const animation = row.animate(
      [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
      { duration: MOVE_MS, easing: EASE_OUT }
    );
    // A row moving up now comes first in the DOM, so it would paint under the rows it passes.
    // Lift it above them for the glide (styled by `[data-flip-lifting]`), then let it settle.
    if (dy > 0) {
      row.dataset.flipLifting = '';
      const settle = () => delete row.dataset.flipLifting;
      animation?.finished.then(settle, settle);
    }
  }
  // A whole new table (a new day group) pops in by itself; that is fine.
  for (const row of pops) {
    row.animate(
      [
        { opacity: 0, transform: 'scale(0.96)' },
        { opacity: 1, transform: 'scale(1.01)', offset: 0.7 },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: POP_MS, easing: EASE_OUT }
    );
  }
}

/** Test hook. */
export function resetRowMotion() {
  captured = null;
}
